import { useRemoveProperty } from '@client/hooks/use-element-properties.hooks';
import { useTrigger } from '@client/hooks/use-utils.hooks';
import { DeleteForever, Edit } from '@mui/icons-material';
import {
  isEditablePropertyName,
  isFullyEditableProperty,
} from '@shared/helpers/element-property.helpers';
import { IElementProperty } from '@shared/models/element-property.model';
import { OneOfPropertyElements } from '@shared/models/project.model';
import React, { useMemo } from 'react';

import { KebabMenu, KebabMenuProps } from '@ui/menus/KebabMenu';
import { useIsReadonlyElement } from '@client/hooks/use-user.hooks';
import { isDataTableChildProperty } from '@shared/helpers/data-tables';

interface IElementPropertyKebabMenuInput extends Omit<
  KebabMenuProps,
  'property'
> {
  element: OneOfPropertyElements;
  property: IElementProperty;
}

const ElementPropertyKebabMenu: React.FC<IElementPropertyKebabMenuInput> = ({
  property,
  element,
  onEdit,
  ...props
}) => {
  const removeProperty = useRemoveProperty(element);
  const deleteProperty = useTrigger(removeProperty, property.id);
  const isEditableName = isEditablePropertyName(property);
  const readonly = useIsReadonlyElement(element);
  const hidden =
    readonly ||
    !isFullyEditableProperty(property) ||
    isDataTableChildProperty(property);

  const items = useMemo(() => {
    return [
      {
        id: 'rename',
        label: 'Rename',
        icon: <Edit color="secondary" />,
        onClick: onEdit,
        disabled: !isEditableName,
      },
      {
        id: 'delete',
        label: 'Delete',
        icon: <DeleteForever color="secondary" />,
        onClick: deleteProperty,
      },
    ];
  }, [deleteProperty, onEdit, isEditableName]);

  return (
    <KebabMenu
      {...props}
      id={property.id}
      items={items}
      margin={0}
      hidden={hidden}
    />
  );
};

export default ElementPropertyKebabMenu;
